/**
 * Landing Page — Thin composition wrapper
 *
 * All sections are split into individual files under ./sections/.
 * This file composes them and provides the props contract consumed by App.tsx.
 *
 * Copy is governed by marketing ruling M1 (2026-10-20), Phase A: process
 * claims only, no performance figure, no live wording. The section order is
 * the ruling's page order. `LandingCopyLint.test.tsx` enforces the ruling's
 * forbidden-word list over the whole rendered page (K-M5 / A-K5).
 *
 * Implementation plan P0-09 (2026-10-22, R71 M5 "now"): the page says the
 * product is in development, never "pre-registered" (no such platform feature
 * exists), and shows no Managed price.
 */

import React from 'react';
import { useScrollDepth } from '@/hooks/useScrollDepth';
import { useSeo } from '@/hooks/useSeo';
import { trackEvent } from '@/lib/analytics';
import {
  Nav,
  Hero,
  TrustBar,
  Features,
  HowItWorks,
  WhatItHolds,
  ProofSection,
  IsThisForYou,
  FirmInvite,
  Pricing,
  CTASection,
  Footer,
} from './sections';

// ============================================================================
// Props (consumed by LandingPageWrapper in App.tsx)
// ============================================================================

export interface LandingPageProps {
  onSignIn?: () => void;
  onSignUp?: (tier?: string) => void;
  onGoToDashboard?: () => void;
  isSignedIn?: boolean;
}

export const LANDING_SEO_TITLE = 'Mendl Labs | Your limits, enforced by code';
export const LANDING_SEO_DESCRIPTION =
  'Mendl Labs is in development and looking for design-partner managers. Today it runs a small library of documented, published investment rules on paper accounts, inside the risk limits you sign. An AI can propose the plan; it cannot place an order or raise a limit. Live trading is not available yet.';

// ============================================================================
// Composed Landing Page
// ============================================================================

export const LandingPage: React.FC<LandingPageProps> = ({
  onSignIn,
  onSignUp,
  onGoToDashboard,
  isSignedIn = false,
}) => {
  useSeo({
    title: LANDING_SEO_TITLE,
    description: LANDING_SEO_DESCRIPTION,
    canonicalPath: '/',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Mendl Labs',
        url: 'https://mendllabs.ai',
        logo: 'https://mendllabs.ai/logo.svg',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Mendl Labs',
        url: 'https://mendllabs.ai',
        description:
          'In development: documented investment rules, run inside limits you sign, with every proposal, approval and order recorded. Paper accounts open; live trading not available yet.',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Mendl Labs',
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'Web',
        url: 'https://mendllabs.ai',
        description:
          'In development. A portfolio built from a small library of documented, published investment rules and run on a paper account inside risk limits the user signs. An AI proposes; deterministic code enforces the limits.',
        featureList: [
          'Signed mandate with position, daily-loss and drawdown limits',
          'AI-proposed portfolio from a library of documented rules',
          'Limits enforced by deterministic code, outside the AI',
          'Paper account at Alpaca',
          'Every proposal, approval and order recorded',
        ],
      },
    ],
  });

  useScrollDepth(80, () => trackEvent('scroll_depth_reached', { pct: 80 }));

  return (
    <div className="min-h-screen bg-[hsl(225,20%,4%)] overflow-x-hidden">
      <Nav
        onSignIn={onSignIn}
        onSignUp={onSignUp}
        onGoToDashboard={onGoToDashboard}
        isSignedIn={isSignedIn}
      />
      <Hero onSignUp={onSignUp} />
      <TrustBar />
      <Features onSignUp={onSignUp} />
      <HowItWorks />
      <WhatItHolds />
      <ProofSection />
      <IsThisForYou />
      <FirmInvite />
      <Pricing onSignUp={onSignUp} />
      <CTASection onSignUp={onSignUp} />
      <Footer />
    </div>
  );
};

/**
 * Backward-compatible default content export.
 * The new LandingPage no longer needs external content props —
 * all content is embedded in the section components.
 * This stub keeps existing imports from breaking.
 */
export const defaultLandingContent = {} as LandingPageProps;
